import type { DataFlowBuilder } from '@/engine/DataFlow';
import type { AlgorithmExtension } from '@/engine/DataFlow/DataFlow';
import * as DT from './DataTypes';
import * as Algo from './Functors';
import * as TTVRAlgo from '@/surgeries/cardiac/TTVR/TTVRAlgorithmHelpers';
import { buildPointToPlaneDistanceMeasurementSigned } from '@/surgeries/cardiac/common/CardiacAlgorithmBuilderCommon';
import type { IValue } from '../DataFlow/Graph';

export function createValueAlgorithmBuilder(descriptor: string, value: IValue): AlgorithmExtension {
  return (builder: DataFlowBuilder): void => {
    builder.name(descriptor).define(value);
  };
}

export function createSplineAlgorithmBuilder(splineDescriptor: string): AlgorithmExtension {
  return (builder: DataFlowBuilder): void => {
    builder.name(splineDescriptor).declare(DT.Polyline);
  };
}

export function createCustomAreaMeasurementAlgorithmBuilder(
  splineDescriptor: string,
  areaMeasDescriptor: string
): AlgorithmExtension {
  return (builder: DataFlowBuilder): void => {
    builder.name(areaMeasDescriptor).bind(Algo.areaMeasurement, DT.AreaMeasurement, splineDescriptor);
  };
}

export function createCustomSplineMeasurementAlgorithmBuilder(
  splineDescriptor: string,
  splineMeasDescriptor: string
): AlgorithmExtension {
  return (builder: DataFlowBuilder): void => {
    builder.name(splineMeasDescriptor).bind(Algo.splineMeasurement, DT.SplineMeasurement, splineDescriptor);
  };
}

export function createVariableDistanceAlgorithmBuilder(descriptor: string): AlgorithmExtension {
  return (builder: DataFlowBuilder): void => {
    builder.name('tp.pt_' + descriptor).bindRaw(
      Algo.tuneValue,
      DT.Point,
      builder.name('tp.pt_' + descriptor + '.dflt').define(Algo.emptyPoint()),
      builder.name('tp.pt_' + descriptor + '.user').define(Algo.emptyPoint()),
      builder.name('tp.pt_' + descriptor + '.appr').define(Algo.emptyPoint()),
      builder.name('tp.pt_' + descriptor + '.flag').define(new DT.TuneFlag(DT.ETuneFlag.USER)) // [chebotarev]: USER must be here
    );

    builder
      .name('tp.pt_' + descriptor + '.confirmed')
      .bind(Algo.ensureNotDefaultValue, DT.Point, 'tp.pt_' + descriptor, 'tp.pt_' + descriptor + '.flag');

    builder
      .name(descriptor + '.quarter')
      .bind(
        TTVRAlgo.findAnnulusQuarterIndexTTVR,
        DT.NumberT,
        'tp.pt_' + descriptor + '.confirmed',
        'landing_zone_area',
        'tp.ap_direction'
      );

    const measInfo = buildPointToPlaneDistanceMeasurementSigned(
      builder,
      'tp.pt_' + descriptor + '.confirmed',
      'pl_annulus'
    );

    builder
      .name('var_' + descriptor + '_height')
      .bindRaw(
        Algo.makeDistanceMeasurement,
        DT.DistanceMeasurement,
        measInfo.distance,
        measInfo.line,
        builder.bindRaw(
          Algo.makeRoiForHorizontalLine,
          DT.RegionOfInterest,
          measInfo.line,
          builder.existing('world_top_direction'),
          builder.existing('world_anterior_direction')
        )
      );

    builder.name(descriptor).bind(Algo.reference, DT.DistanceMeasurement, 'var_' + descriptor + '_height');
  };
}

export function createCommissurePointAlgorithmBuilder(descriptor: string): AlgorithmExtension {
  return (builder: DataFlowBuilder): void => {
    builder.name('vec_' + descriptor + '_indicated').declare(DT.Vector);

    builder
      .name('tp.' + descriptor + '_rotation')
      .bindRaw(
        Algo.tuneValue,
        DT.NumberT,
        builder.name('tp.' + descriptor + '_rotation.dflt').define(new DT.NumberT(0)),
        builder.name('tp.' + descriptor + '_rotation.user').define(new DT.NumberT(0)),
        builder.name('tp.' + descriptor + '_rotation.appr').define(new DT.NumberT(0)),
        builder.name('tp.' + descriptor + '_rotation.flag').define(Algo.defaultTuneFlag())
      );

    builder
      .name(descriptor)
      .bind(
        TTVRAlgo.findCommissurePointTTVR,
        DT.Point,
        'landing_zone_spline',
        'vec_' + descriptor + '_indicated',
        'tp.' + descriptor + '_rotation'
      );
  };
}
